import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import {
  NavLink,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom'

import './styles.scss'

import { useSizeClass } from '../../utils/useWindowSize'
import { getActiveSegment,getBaseUrlFromPathName } from '../Nav/navUtils'
import { NavMobile } from './NavMobile'
import { useNavigationLinks } from './useNavigationLinks'

export const Nav = () => {
  const { isMobile } = useSizeClass()
  const { forceEnableTimeTracking } = useOutletContext<{
    forceEnableTimeTracking?: boolean
  }>()
  const navigate = useNavigate()
  const location = useLocation()
  const navMenuRef = useRef<HTMLDivElement>(null)
  const lineRef = useRef<HTMLDivElement>(null)

  const allNavLinks = useNavigationLinks({
    forceEnableTimeTracking,
  })

  const storedSegment =
    localStorage.getItem('activeLink') || getActiveSegment(location.pathname)

  const [activeSegment, setActiveSegment] = useState(storedSegment)
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    if (isMobile) return

    const el = navMenuRef.current?.querySelector(
      `a[data-path='${storedSegment}']`,
    ) as HTMLAnchorElement | null

    if (el && lineRef.current) {
      const { offsetLeft, offsetWidth } = el
      lineRef.current.style.transition = 'none'
      lineRef.current.style.transform = `translateX(${offsetLeft}px)`
      lineRef.current.style.width = `${offsetWidth}px`
    }

    requestAnimationFrame(() => setHasMounted(true))
  }, [isMobile, storedSegment])

  useEffect(() => {
    if (isMobile) return

    const currentSegment = getActiveSegment(location.pathname)

    setActiveSegment(currentSegment)
    localStorage.setItem('activeLink', currentSegment)

    const el = navMenuRef.current?.querySelector(
      `a[data-path='${currentSegment}']`,
    ) as HTMLAnchorElement | null

    if (el && lineRef.current && hasMounted) {
      const { offsetLeft, offsetWidth } = el
      lineRef.current.style.transition =
        'transform 0.25s ease, width 0.25s ease'
      lineRef.current.style.transform = `translateX(${offsetLeft}px)`
      lineRef.current.style.width = `${offsetWidth}px`
    }
  }, [location.pathname, allNavLinks, hasMounted, isMobile])

  const handleNavLinkClick = (
    to: string,
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault()
    const baseUrl = getBaseUrlFromPathName(location.pathname)
    navigate(`${baseUrl}/${to}`)
  }

  if (isMobile) {
    return <NavMobile />
  }

  return (
    <nav className='accounting-layout__nav'>
      <div className='accounting-layout__nav__wrapper' ref={navMenuRef}>
        {allNavLinks.map(link => (
          <NavLink
            key={link.to}
            to={link.to}
            data-path={link.to}
            className={classNames(
              'accounting-layout__nav__link',
              `accounting-layout__nav__link--${link.to}`,
              activeSegment === link.to
                && 'accounting-layout__nav__link--active',
            )}
            onClick={e => handleNavLinkClick(link.to, e)}
          >
            {link.label}
          </NavLink>
        ))}
        <div ref={lineRef} className='accounting-layout__nav__link-line' />
      </div>
    </nav>
  )
}
